import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from 'react-helmet-async';
import { Table, Button, InputGroup, Input } from 'rsuite';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Loader } from 'rsuite';
import { Pagination } from 'rsuite';
import FileDownloadIcon from '@rsuite/icons/FileDownload';
import { useAuth} from '../../AuthContext';

import SearchIcon from '@rsuite/icons/Search';
import CheckRoundIcon from "@rsuite/icons/CheckRound";
import jsPDF from "jspdf";
import "jspdf-autotable";

const { Column, HeaderCell, Cell } = Table;
  

function Invoices() {
  const { auth } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = React.useState(1);
  const [page, setPage] = useState(0);  
  const [totalPages, setTotalPages] = useState(1);  
  const [searchInput, setSearchInput] = useState('');  
  const navigate = useNavigate(); // Initialize useNavigate

  const generatePDF = (invoice, content) => {
    const doc = new jsPDF();

    // Add header
    doc.setFontSize(10);
    doc.text(`Invoice No: ${invoice.id}`, 10, 10);

    if (invoice.positive) {
        doc.text(`Invoice Date: ${invoice.invoiceDate}`, 10, 15);
        doc.text(`Terms: 60 days net`, 10, 20);
    } else {
        doc.text(`Date: ${invoice.invoiceDate}`, 10, 15);
        doc.text(`Credit invoice`, 10, 20);
    }

    doc.text(`Due Date: ${invoice.dueDate}`, 10, 25);
    doc.text(`Late Payment Interest: 10%`, 10, 30);

    // Invoice details
    doc.text(`Delticom AG`, 170, 10);
    doc.text(`SE502061326001`, 170, 15);
    doc.text(`Postfach 1265`, 170, 20);
    doc.text(`D-17320 Pasewalk`, 170, 25);
    doc.text(`Deutchland`, 170, 30);

    // Table for invoice content
    const tableColumns = ["Order Number", "Product Number", "Description", "Quantity", "Price Each", "Total"];
    const tableRows = content.map(item => [
        item.orderNr,
        item.prodNr,
        item.description,
        item.quantityOrdered,
        item.priceAfterAddon.toFixed(2),
        item.priceWithQuantity.toFixed(2),
    ]);

    doc.autoTable({
        head: [tableColumns],
        body: tableRows,
        startY: 35,
        theme: "grid",
        margin: { left: 10, right: 10 },
        pageBreak: 'auto',
    });

    // Get the Y position after the table (across pages)
    let finalY = doc.lastAutoTable.finalY;

    // Check if there's enough space, else add a new page
    if (finalY + 80 > doc.internal.pageSize.getHeight()) {
        doc.addPage();
        finalY = 10;  // Reset Y position on new page
    }

    // Payment instructions
    doc.text(`Please pay to Bankgiro 155-5044 or to the following Account`, 10, finalY + 10);

    doc.text(`Subtotal:`, 165, finalY + 10);
    doc.text(`VAT:`, 165, finalY + 15);
    doc.text(`Total:`, 165, finalY + 20);

    doc.text(`${invoice.priceWithoutVat.toFixed(2)}`, 185, finalY + 10);
    doc.text(`${invoice.vat.toFixed(2)}`, 185, finalY + 15);
    doc.text(`${invoice.priceWithVat.toFixed(2)}`, 185, finalY + 20);

    doc.text(`Bank:`, 10, finalY + 20);
    doc.text(`Account/IBAN:`, 10, finalY + 25);
    doc.text(`BIC/SWIFT:`, 10, finalY + 30);

    doc.text(`Address:`, 10, finalY + 40);
    doc.text(`Phone:`, 10, finalY + 60);
    doc.text(`Org.no/VAT:`, 10, finalY + 65);
    doc.text(`Bankgiro:`, 10, finalY + 70);
    doc.text(`Email:`, 10, finalY + 75);

    doc.text(`Swedbank AB, 105 34 Stockholm`, 40, finalY + 20);
    doc.text(`SE2080000832796946018170`, 40, finalY + 25);
    doc.text(`SWEDSESS`, 40, finalY + 30);

    doc.text(`Däck och Fälg i Skandinavien AB`, 40, finalY + 40);
    doc.text(`Vattenkraftsvägen 11B`, 40, finalY + 45);
    doc.text(`135 70 Stockholm`, 40, finalY + 50);

    doc.text(`+46-8-647 00 66 www.infosite.se`, 40, finalY + 60);
    doc.text(`SE559207172301`, 40, finalY + 65);
    doc.text(`155-5044`, 40, finalY + 70);
    doc.text(`info@infosite.se`, 40, finalY + 75);

    // Save the PDF
    doc.save(`invoice_delticom_${invoice.id}.pdf`);
};


  const fetchInvoices = async (currentPage = 0, queryId = '') => {
      setLoading(true);
  
      try{
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/invoice/invoices?page=${currentPage}&size=15&queryId=${encodeURIComponent(queryId)}`, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.token}`
          }
        });
  
        if(response.ok){
          const data = await response.json();
          setInvoices(data.content);
          setPage(data.number);
          setTotalPages(data.totalPages);
        }
        else{
          const errorText = await response.text();
          throw new Error(`Misslyckade att hämta fakturor: ${errorText}`);
        }
      }catch(error){
        toast.error(error.message, {
          position: 'bottom-right'
        });
      } finally{
        setLoading(false);
      }
    }

    const markAsPaid = async (invoice) => {
      setLoading(true);
    
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/invoice/mark?invoiceId=${invoice.id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.token}`
          }
        });
    
        const result = await response.json();
    
        if (response.ok) {
          // Update the specific invoice in the state
          setInvoices((prevInvoices) =>
            prevInvoices.map((inv) =>
              inv.id === invoice.id ? { ...inv, paid: true } : inv // Update only the matching invoice
            )
          );
    
          toast.info("Faktura betalad", {
            position: 'bottom-right'
          });
        } else {
          toast.error(result.message, {
            position: 'bottom-right'
          });
        }
      } catch (error) {
        console.log(error);
        toast.error('Misslyckades att markera faktura som betalad: ' + error.message, {
          position: 'bottom-right'
        });
      } finally {
        setLoading(false);
      }
    };
    

    const downloadInvoice = async (invoice) => {
      setLoading(true);
  
      try{
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/invoice/orders?invoiceId=${invoice.id}`, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.token}`
          }
        });
  
        if(response.ok){
          const data = await response.json();
          generatePDF(invoice, data)
        }
        else{
          const errorText = await response.text();
          throw new Error(`Misslyckade att hämta innehåll: ${errorText}`);
        }
      }catch(error){
        toast.error(error.message, {
          position: 'bottom-right'
        });
      } finally{
        setLoading(false);
      }
    }

    function search(queryId) {
        setSearchInput(queryId);  // Update the searchInput state
        setPage(0);              // Reset to the first page
        fetchInvoices(0, queryId); // Fetch the first page with the search term
      }
    
      useEffect(() => {
        fetchInvoices();
      }, [])

  return (
    <>
      <Helmet>
        <title>{`Fakturor`}</title>
      </Helmet>
      <div className="px-5 pt-4 w-100">
        <h4 className="mb-2">Fakturor</h4>
        <p className="text-muted">Översikt av infosites fakturor.</p>
        <hr />
        <div className="d-flex justify-content-end">
            <div className="d-flex mb-2">
                <div>
                <InputGroup>
                    <Input size="md" placeholder={'Sök fakturanummer'} onChange={(value) => search(value)} />
                    <InputGroup.Button>
                    <SearchIcon />
                    </InputGroup.Button>
                </InputGroup>
                </div>
            </div>
        </div>


        <Table
          className='rounded'
          autoHeight
          cellBordered
          data={invoices}
          bordered
          autoWidth
        >
          {/* Define the columns with appropriate width */}
          <Column flexGrow={1}>
            <HeaderCell>Order nummer</HeaderCell>
            <Cell dataKey="id" />
          </Column>

          <Column width={150} align='center'>
            <HeaderCell>Ex moms</HeaderCell>
            <Cell dataKey="priceWithoutVat" />
          </Column>

          <Column width={150} align='center'>
            <HeaderCell>Ink moms</HeaderCell>
            <Cell dataKey="priceWithVat" />
          </Column>
  
          <Column width={150} align='center'>
            <HeaderCell>Datum</HeaderCell>
            <Cell dataKey="dateCreated" />
          </Column>
  
          <Column width={170} align="center">
            <HeaderCell>Hanterad</HeaderCell>
            <Cell>
              {(rowData) =>
                !rowData.paid ? (
                  <span
                  className='text-primary'
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => markAsPaid(rowData)} // Pass the selected row's data (invoice) to the method
                  >
                    Markera som betalad
                  </span>
                ) : null
              }
            </Cell>
          </Column>


            <Column width={80} align='center'>
            <HeaderCell>Betalad</HeaderCell>
            <Cell>
            {(rowData) =>
            rowData.paid ? (
              <CheckRoundIcon style={{ color: "green" }} />
            ) : null
            }
            </Cell>
            </Column>

            <Column width={100} align="center">
              <HeaderCell>Ladda ner PDF</HeaderCell>
              <Cell>
                {(rowData) => (
                  <FileDownloadIcon
                    style={{ cursor: "pointer" }}
                    onClick={() => downloadInvoice(rowData)} // Pass the row data (invoice) to the function
                  />
                )}
              </Cell>
            </Column>

        </Table>
        <Pagination
                  size="md"
                  className="mt-2"
                  total={totalPages * 15} // 15 is the limit of the results returned
                  limit={15}
                  activePage={activePage}
                  onChangePage={(page) => {
                    setActivePage(page);       // Update the active page
                    fetchInvoices(page - 1, searchInput); // Include the current search input
                  }}
                />
      </div>
    </>
  );
}

export default Invoices;
